import React, { useState } from 'react';
import { Card, Badge, ProgressBar, Button } from 'react-bootstrap';
import { FaPlay, FaEdit, FaTrash, FaStar, FaBook } from 'react-icons/fa';
import PropTypes from 'prop-types';

import './VideoCard.css';

const VideoCard = ({ video, onPlay, onEdit, onDelete }) => {
  const [imageError, setImageError] = useState(false);

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  const calculateProgress = () => {
    if (!video.duration || video.duration === 0) return 0;
    return Math.round((video.watch_progress / video.duration) * 100);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPlay(video);
    }
  };

  return (
    <Card className="video-card">
      <div
        className="video-thumbnail-container"
        onClick={() => onPlay(video)}
        onKeyDown={handleKeyPress}
        role="button"
        tabIndex={0}
        aria-label={`Play ${video.title}`}
      >
        {!imageError && video.thumbnail_url ? (
          <Card.Img
            variant="top"
            src={video.thumbnail_url}
            alt={video.title}
            onError={handleImageError}
            className="video-thumbnail"
          />
        ) : (
          <div className="video-thumbnail-placeholder">
            <FaPlay size={48} />
          </div>
        )}
        {video.is_completed && (
          <Badge bg="success" className="completion-badge">
            Completed
          </Badge>
        )}
        {video.is_ai_recommended && (
          <Badge bg="primary" className="ai-badge">
            <FaStar /> AI Recommended
          </Badge>
        )}
        <div className="play-overlay">
          <FaPlay size={32} />
        </div>
      </div>

      <Card.Body>
        <Card.Title className="video-title">{video.title}</Card.Title>

        {video.channel_name && (
          <div className="video-channel">
            <small className="text-muted">{video.channel_name}</small>
          </div>
        )}

        {video.description && (
          <Card.Text className="video-description">
            {video.description.length > 100
              ? `${video.description.substring(0, 100)}...`
              : video.description}
          </Card.Text>
        )}

        {video.ml_courses_staging && (
          <div className="video-course-tag mb-2">
            <Badge bg="info" className="me-1">
              <FaBook className="me-1" />
              {video.ml_courses_staging.title}
            </Badge>
          </div>
        )}

        {video.tags && video.tags.length > 0 && (
          <div className="video-tags mb-2">
            {video.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} bg="secondary" className="me-1 mb-1">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {video.is_ai_recommended && video.recommendation_reason && (
          <div className="recommendation-reason mb-2">
            <small className="text-info">
              <FaStar className="me-1" />
              {video.recommendation_reason}
            </small>
          </div>
        )}

        {video.watch_progress > 0 && (
          <div className="video-progress mb-2">
            <small className="text-muted">Progress: {calculateProgress()}%</small>
            <ProgressBar
              now={calculateProgress()}
              variant={video.is_completed ? 'success' : 'info'}
              className="mt-1"
            />
          </div>
        )}

        {video.playback_position_seconds > 0 && !video.is_completed && (
          <div className="video-resume mb-2">
            <Badge bg="warning" text="dark">
              Resume at {formatDuration(video.playback_position_seconds)}
            </Badge>
          </div>
        )}

        <div className="video-meta">
          <small className="text-muted">
            {video.duration && `Duration: ${formatDuration(video.duration)}`}
          </small>
        </div>

        <div className="video-actions mt-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(video);
            }}
            className="icon-only-btn"
            title="Edit video"
            aria-label="Edit video"
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(video);
            }}
            className="icon-only-btn"
            title="Delete video"
            aria-label="Delete video"
          >
            <FaTrash />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

VideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    video_url: PropTypes.string.isRequired,
    thumbnail_url: PropTypes.string,
    duration: PropTypes.number,
    channel_name: PropTypes.string,
    watch_progress: PropTypes.number,
    is_completed: PropTypes.bool,
    is_ai_recommended: PropTypes.bool,
    recommendation_reason: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    ml_courses_staging: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
    }),
  }).isRequired,
  onPlay: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default VideoCard;
